import React, { useState, useEffect } from 'react';
import { useLanguage } from './components/LanguageContext';
import { fetchWithAuth, API_URL } from './utils/api';
import { getUser, setUserSession, clearUserSession, isAdmin } from './utils/AuthUtils';
import LanguageToggle from './components/LanguageToggle';
import Dashboard from './components/Dashboard';
import Patients from './components/Patients';
import Appointments from './components/Appointments';
import DentistQueue from './components/DentistQueue';
import DentistManagement from './components/DentistManagement';
import Treatments from './components/Treatments';
import Billing from './components/Billing';
import Branches from './components/Branches';
import OdontogramPage from './components/OdontogramPage';
import MedicalCertificates from './components/MedicalCertificates';
import Shop from './components/Shop';
import Medications from './components/Medications';
import DrugLabelSettings from './components/DrugLabelSettings';
import { visibleNavItems, flattenNavItems, NAV_ITEMS } from './navItems';
import useIsMobile from './hooks/useIsMobile';
import MobileHeader from './components/MobileHeader';
import NavDrawer from './components/NavDrawer';
import BottomNav from './components/BottomNav';
import {
  Stethoscope, Building2, LogOut, Menu, X, ChevronDown, ChevronRight,
} from 'lucide-react';

function App() {
  const { print_lang, loadBranchLanguage, currentLanguage } = useLanguage();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [currentBranch, setCurrentBranch] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  // Which grouped sections are expanded, keyed by group id.
  const [openGroups, setOpenGroups] = useState({});
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Check for existing session on mount
  useEffect(() => {
    const savedUser = getUser();
    const token = localStorage.getItem('token');
    if (savedUser && token) {
      setUser(savedUser);
      setIsLoggedIn(true);
      fetchUserBranch(savedUser.branch_id);
    }
  }, []);

  // Load language when branch changes
  useEffect(() => {
    if (currentBranch?.branch_id) {
      loadBranchLanguage(currentBranch.branch_id);
    }
  }, [currentBranch, loadBranchLanguage]);

  // Fetch user's branch info
  const fetchUserBranch = async (branchId) => {
    try {
      const data = await fetchWithAuth(`/api/branches/${branchId}`);
      if (data) {
        setCurrentBranch(data);
      }
    } catch (error) {
      console.error('Failed to fetch branch:', error);
    }
  };

  // Fetch all branches (for admin Branches page)
  const fetchBranches = async () => {
    try {
      await fetchWithAuth('/api/branches');
    } catch (error) {
      console.error('Failed to fetch branches:', error);
    }
  };

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setLoginError('');

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });

      const data = await response.json();

      if (data.success) {
        setUserSession(data.token, data.user);
        setUser(data.user);
        setIsLoggedIn(true);
        fetchUserBranch(data.user.branch_id);
      } else {
        setLoginError(data.message || 'Login failed');
      }
    } catch (error) {
      setLoginError('Connection error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    clearUserSession();
    setUser(null);
    setIsLoggedIn(false);
    setCurrentBranch(null);
  };

  // Navigation items live in ./navItems as data (the phone shell needs them
  // as data, not JSX). An entry with `children` is a collapsible group rather
  // than a page; its children keep their OWN adminOnly, so a receptionist sees
  // the group holding only what they may actually open, and an empty group is
  // not rendered at all.
  const navItems = visibleNavItems(isAdmin);

  // The phone header shows a single title for the active page. activeTab can
  // be a top-level id or a Branch Setting child id, so the lookup flattens
  // NAV_ITEMS rather than searching only the top level.
  const currentNavItem = flattenNavItems(NAV_ITEMS).find((item) => item.id === activeTab);
  const pageTitle = currentNavItem
    ? print_lang(currentNavItem.labelKey, currentNavItem.labelFallback)
    : print_lang('dashboard', 'Dashboard');

  const isVisible = (item) => !item.adminOnly || isAdmin();

  // A-Z on the name actually on screen, so the group reads alphabetically in
  // Thai as well as English rather than only in the source order. Falls back to
  // a plain comparison where Intl has no data for the language.
  const sortedChildren = (children) =>
    children
      .filter(isVisible)
      .slice()
      .sort((a, b) => print_lang(a.labelKey, a.labelFallback)
        .localeCompare(print_lang(b.labelKey, b.labelFallback), currentLanguage));

  const toggleGroup = (id) => {
    // Collapsed rail has no room for a submenu; opening a group there opens the
    // sidebar too, rather than expanding into 20px of width.
    if (!sidebarOpen) setSidebarOpen(true);
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Render login page
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
              <Stethoscope className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800">Dental Clinic</h1>
            <p className="text-gray-500 mt-2">ระบบบริหารจัดการคลินิกทันตกรรม</p>
          </div>

          <form onSubmit={handleLogin}>
            {loginError && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">
                {loginError}
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={loginData.email}
                onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                className="w-full"
                placeholder="your@email.com"
                required
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                type="password"
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                className="w-full"
                placeholder="••••••••"
                required
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full btn btn-primary py-3"
            >
              {isLoading ? 'กำลังเข้าสู่ระบบ...' : 'เข้าสู่ระบบ'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Render main application
  return (
    <div className="flex min-h-screen bg-gray-100">
      {/* Sidebar - desktop only. The phone shell (MobileHeader / NavDrawer /
          BottomNav) replaces it below 768px. */}
      {!isMobile && (
      <aside className={`sidebar sidebar-desktop ${sidebarOpen ? 'w-64' : 'w-20'} transition-all duration-300 flex flex-col`}>
        {/* Logo */}
        <div className="p-4 flex items-center justify-between border-b border-white/10">
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              <Stethoscope className="w-8 h-8" />
              <span className="font-bold text-lg">Dental Clinic</span>
            </div>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-white/10 rounded"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4">
          {navItems.map((item) => {
            const Icon = item.Icon;

            if (item.children) {
              const children = sortedChildren(item.children);
              // Every child hidden means the group itself has nothing to offer.
              if (children.length === 0) return null;

              const holdsActive = children.some((c) => c.id === activeTab);
              // Expanded when the user opened it, and always while one of its
              // pages is the active one — otherwise landing on a child page
              // (a reload, say) shows a collapsed group with nothing marked.
              const expanded = openGroups[item.id] ?? holdsActive;

              return (
                <div key={item.id}>
                  <button
                    onClick={() => toggleGroup(item.id)}
                    className={`sidebar-item w-full ${holdsActive && !expanded ? 'active' : ''}`}
                    aria-expanded={expanded}
                  >
                    <Icon size={20} />
                    {sidebarOpen && (
                      <>
                        <span className="ml-3">{print_lang(item.labelKey, item.labelFallback)}</span>
                        <span className="ml-auto">
                          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </span>
                      </>
                    )}
                  </button>

                  {sidebarOpen && expanded && children.map((child) => {
                    const ChildIcon = child.Icon;
                    return (
                      <button
                        key={child.id}
                        onClick={() => setActiveTab(child.id)}
                        className={`sidebar-item sidebar-subitem w-full ${activeTab === child.id ? 'active' : ''}`}
                      >
                        <ChildIcon size={18} />
                        <span className="ml-3">{print_lang(child.labelKey, child.labelFallback)}</span>
                      </button>
                    );
                  })}
                </div>
              );
            }

            // Hide admin-only items for non-admins
            if (!isVisible(item)) return null;

            return (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`sidebar-item w-full ${activeTab === item.id ? 'active' : ''}`}
              >
                <Icon size={20} />
                {sidebarOpen && (
                  <span className="ml-3">{print_lang(item.labelKey, item.labelFallback)}</span>
                )}
              </button>
            );
          })}
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-white/10">
          {sidebarOpen && (
            <div className="mb-3">
              <p className="font-medium truncate">{user?.name}</p>
              <p className="text-sm opacity-70 truncate">{user?.email}</p>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="sidebar-item w-full justify-center"
          >
            <LogOut size={20} />
            {sidebarOpen && <span className="ml-3">{print_lang('logout', 'Logout')}</span>}
          </button>
        </div>
      </aside>
      )}

      {isMobile && (
        <>
          <MobileHeader
            title={pageTitle}
            onOpenDrawer={() => setDrawerOpen(true)}
            user={user}
            currentBranch={currentBranch}
          />
          <NavDrawer
            open={drawerOpen}
            user={user}
            currentBranch={currentBranch}
            currentPage={activeTab}
            onNavigate={setActiveTab}
            onLogout={handleLogout}
            onClose={() => setDrawerOpen(false)}
            isAdmin={isAdmin}
          />
        </>
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Desktop only. MobileHeader already shows the branch and the
            language toggle in the fixed bar; keeping this one would stack
            two headers. */}
        <header className="hidden md:flex bg-white shadow-sm px-6 py-4 items-center justify-between">
          <div className="flex items-center gap-3">
            {currentBranch && (
              <>
                {currentBranch.logo_path ? (
                  <img
                    src={currentBranch.logo_path.startsWith('http') ? currentBranch.logo_path : `${API_URL}/${currentBranch.logo_path}`}
                    alt={currentBranch.name}
                    className="h-10 object-contain"
                  />
                ) : (
                  <Building2 size={28} className="text-gray-400" />
                )}
                <span className="text-gray-600 font-medium">{currentBranch.name}</span>
              </>
            )}
          </div>

          <div className="flex items-center gap-4">
            {/* Language Toggle */}
            <LanguageToggle currentBranch={currentBranch} />
          </div>
        </header>

        {/* Page Content */}
        <div className="p-6">
          {activeTab === 'dashboard' && (
            <Dashboard currentBranch={currentBranch} user={user} />
          )}
          {activeTab === 'queue' && (
            <DentistQueue currentBranch={currentBranch} user={user} />
          )}
          {activeTab === 'appointments' && (
            <Appointments currentBranch={currentBranch} user={user} />
          )}
          {activeTab === 'patients' && (
            <Patients currentBranch={currentBranch} user={user} />
          )}
          {activeTab === 'odontogram' && (
            <OdontogramPage currentBranch={currentBranch} user={user} />
          )}
          {activeTab === 'treatments' && (
            <Treatments currentBranch={currentBranch} user={user} />
          )}
          {activeTab === 'shop' && (
            <Shop currentBranch={currentBranch} user={user} />
          )}
          {activeTab === 'medications' && (
            <Medications currentBranch={currentBranch} user={user} />
          )}
          {activeTab === 'label_settings' && (
            <DrugLabelSettings currentBranch={currentBranch} user={user} />
          )}
          {activeTab === 'certificates' && (
            <MedicalCertificates currentBranch={currentBranch} user={user} />
          )}
          {activeTab === 'billing' && (
            <Billing currentBranch={currentBranch} user={user} />
          )}
          {activeTab === 'dentists' && (
            <DentistManagement currentBranch={currentBranch} user={user} />
          )}
          {activeTab === 'branches' && (
            <Branches
              currentBranch={currentBranch}
              user={user}
              onBranchUpdate={fetchBranches}
              onCurrentBranchUpdate={() => fetchUserBranch(user.branch_id)}
            />
          )}
        </div>
      </main>

      {isMobile && (
        <BottomNav
          currentPage={activeTab}
          onNavigate={setActiveTab}
          onLogout={handleLogout}
          isAdmin={isAdmin}
        />
      )}
    </div>
  );
}

export default App;
